<!--
    Basic layout for drawer contents with header + footer

-->
<script setup lang="ts">
/* UTILS */
import { ALButton, ALDivider } from '@af-lib/vue-ui';
import { nextTick, onMounted, ref } from 'vue';

/* COMPONENTS */
import IconWithHover from '@/components/icons/IconWithHover.vue';
import Text from '@/components/text/Text.vue';

/* PROPS */
type Props = {
  title?: string;
  icon?: string;
  flexContent?: boolean;
  datacyPrefix?: string;
  noFooter?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  icon: 'fa-solid fa-pen',
  flexContent: false,
  noFooter: false
});

const emits = defineEmits(['close']);

const closeBtnRef = ref<HTMLButtonElement | null>(null);

onMounted(() => {
  nextTick(() => {
    closeBtnRef.value?.focus();
  });
});
</script>

<template>
  <!-- HEADER -->
  <div class="min-h-screen h-full flex flex-col text-af-grey-600">
    <div class="title-section">
      <div class="flex align-center">
        <div class="flex justify-center align-center bubble">
          <FaIcon class="text-icon-5 font-weight-black" :icon="props.icon" aria-hidden="true" focusable="false" />
        </div>
        <div class="flex justify-space-between align-center w-100">
          <Text
            v-if="props.title"
            type="h3"
            class="ms-3 text-fontPrimary text-h3"
            :data-cy="props.datacyPrefix ? `${props.datacyPrefix}-title` : undefined"
          >
            {{ props.title }}
          </Text>

          <slot name="titleInfo"></slot>
          <div :class="['flex align-center', props.title ? 'ms-auto' : '']">
            <slot name="headerInfos"></slot>
          </div>

          <button
            ref="closeBtnRef"
            class="me-5 ms-3"
            :aria-label="$t('general.close')"
            :data-cy="props.datacyPrefix ? `${props.datacyPrefix}-cross-btn` : undefined"
            @click="emits('close')"
          >
            <IconWithHover class="text-subtitle-1" icon="fa-xmark-large" />
          </button>
        </div>
      </div>
    </div>
    <ALDivider />

    <!-- CONTENT-->
    <div
      class="content-section flex-auto flex flex-column overflow-auto"
      :data-cy="props.datacyPrefix ? `${props.datacyPrefix}-content` : undefined"
    >
      <slot></slot>
    </div>

    <!-- FOOTER-->

    <ALDivider />
    <div v-if="!props.noFooter" class="flex flex-column pa-5 footer-section">
      <div v-if="$slots.footerInfos" class="text-sm mb-3">
        <slot name="footerInfos"></slot>
      </div>
      <div class="flex align-center md:ms-auto">
        <slot name="tertiaryBtn">
          <ALButton
            type="tertiary"
            size="m"
            class="me-6 flex-auto w-full"
            :data-cy="props.datacyPrefix ? `${props.datacyPrefix}-cancel` : undefined"
            :label="$t('general.cancel')"
            @click="emits('close')"
          />
        </slot>
        <slot name="primaryBtn"></slot>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.title-section {
  border-top-left-radius: 0.25rem;
  padding: 16px 20px;
  .bubble {
    border-radius: 50%;
    padding: 0.5rem 0.5rem;
    background-color: rgb(var(--v-theme-iconTitleHeader));
    color: white;
    width: 30px;
    height: 30px;
  }
}
.content-section {
  /* New branding/Pink/Pink lighter 4 */
  border-left: 0.188rem solid rgb(var(--v-theme-drawerContentBorderLeft));
}
.footer-section {
  min-height: 5rem;
}
</style>
